import { useEffect, useState, useCallback, Fragment } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Phone, CircleNotch, Plus, Trash, CheckCircle, WarningCircle, Copy,
  PhoneCall, CaretDown, CaretRight, Robot, Sparkle, Tag,
} from "@phosphor-icons/react";

function fmtDuration(call) {
  if (!call.started_at || !call.ended_at) return "—";
  const s = Math.max(0, Math.round((new Date(call.ended_at) - new Date(call.started_at)) / 1000));
  const m = Math.floor(s / 60);
  return m ? `${m}m ${s % 60}s` : `${s}s`;
}

const STATUS_LABEL = { "in-progress": "Pågår", completed: "Avslutat" };

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function qualityColor(score) {
  if (score == null) return "bg-zinc-100 text-zinc-500";
  if (score >= 75) return "bg-emerald-100 text-emerald-700";
  if (score >= 50) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
}

export default function Telephony() {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [calls, setCalls] = useState([]);
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({ number: "", agent_id: "", label: "", provider: "twilio" });
  const [adding, setAdding] = useState(false);
  const [openCall, setOpenCall] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [insights, setInsights] = useState(null);
  const [query, setQuery] = useState("");
  const [drafts, setDrafts] = useState({});

  const fixCall = async (callId) => {
    setDrafts((d) => ({ ...d, [callId]: { loading: true } }));
    try {
      const { data } = await api.post(`/telephony/calls/${callId}/fix`);
      setDrafts((d) => ({ ...d, [callId]: { loading: false, items: data.suggestions || [], agentId: data.agent_id } }));
      toast.success("AI-utkast skapat — granska och lägg till.");
    } catch (e) {
      setDrafts((d) => { const n = { ...d }; delete n[callId]; return n; });
      toast.error(e?.response?.data?.detail || "Kunde inte skapa utkast.");
    }
  };
  const acceptDraft = async (callId, agentId, sid) => {
    try {
      await api.post(`/agents/${agentId}/kb-suggestions/${sid}/accept`);
      toast.success("Tillagt i kunskapsbasen ✓");
      setDrafts((d) => ({ ...d, [callId]: { ...d[callId], items: d[callId].items.filter((s) => s.id !== sid) } }));
    } catch { toast.error("Kunde inte lägga till."); }
  };
  const dismissDraft = async (callId, agentId, sid) => {
    try { await api.post(`/agents/${agentId}/kb-suggestions/${sid}/dismiss`); } catch {}
    setDrafts((d) => ({ ...d, [callId]: { ...d[callId], items: d[callId].items.filter((s) => s.id !== sid) } }));
  };

  const load = useCallback(() => {
    api.get("/telephony/status").then((r) => setStatus(r.data)).catch(() => setStatus(false));
    api.get("/telephony/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/telephony/numbers").then((r) => setNumbers(r.data)).catch(() => {});
    api.get("/telephony/calls").then((r) => setCalls(r.data)).catch(() => {});
    api.get("/telephony/insights").then((r) => setInsights(r.data)).catch(() => {});
    api.get("/agents").then((r) => setAgents(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const addNumber = async (e) => {
    e.preventDefault();
    if (!form.number.trim() || !form.agent_id) { toast.error("Ange nummer och välj en agent."); return; }
    setAdding(true);
    try {
      await api.post("/telephony/numbers", form);
      toast.success("Telefonnummer kopplat.");
      setForm({ number: "", agent_id: "", label: "", provider: "twilio" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kunde inte spara numret");
    } finally { setAdding(false); }
  };

  const removeNumber = async (id) => {
    try { await api.delete(`/telephony/numbers/${id}`); toast.success("Nummer borttaget."); load(); }
    catch { toast.error("Kunde inte ta bort numret"); }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Kopierat"); };

  const analyzeCall = async (id) => {
    setAnalyzing(id);
    try {
      const { data } = await api.post(`/telephony/calls/${id}/analyze`);
      setCalls((cs) => cs.map((c) => (c.id === id ? { ...c, ...data } : c)));
      api.get("/telephony/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/telephony/insights").then((r) => setInsights(r.data)).catch(() => {});
      toast.success("AI-sammanfattning genererad.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kunde inte analysera samtalet");
    } finally { setAnalyzing(null); }
  };

  if (status === null) return <div className="p-20 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#0033FF]" /></div>;

  const configured = status && status.configured;

  return (
    <div className="p-6 sm:p-8" data-testid="telephony-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#0033FF]/10 flex items-center justify-center"><Phone size={22} weight="bold" className="text-[#0033FF]" /></div>
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Telefoni</h1>
          <p className="text-zinc-500">Låt dina AI-agenter svara på riktiga inkommande samtal via Twilio — tal in, tal ut, på svenska.</p>
        </div>
      </div>

      {/* KPI cards */}
      {stats && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="telephony-kpis">
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Samtal totalt</div>
            <div className="mt-1 font-display font-bold text-3xl" data-testid="kpi-total">{stats.total}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Löst av AI (utan människa)</div>
            <div className="mt-1 font-display font-bold text-3xl text-emerald-600" data-testid="kpi-deflection">{stats.total ? `${stats.deflection_rate}%` : "—"}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Överkopplade till människa</div>
            <div className="mt-1 font-display font-bold text-3xl text-violet-600" data-testid="kpi-handover">{stats.handover}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Snittkvalitet</div>
            <div className="mt-1 font-display font-bold text-3xl text-[#0033FF]" data-testid="kpi-quality">{stats.avg_quality != null ? `${stats.avg_quality}/100` : "—"}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">CSAT (uppskattad)</div>
            <div className="mt-1 font-display font-bold text-3xl text-amber-500" data-testid="kpi-csat">{stats.avg_csat != null ? `${stats.avg_csat}/5` : "—"}</div>
          </div>
        </div>
      )}

      {/* Status / setup */}
      <div className={`mt-8 rounded-xl border p-5 ${configured ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="telephony-status">
        <div className="flex items-center gap-2.5">
          {configured
            ? <CheckCircle size={22} weight="fill" className="text-emerald-600" />
            : <WarningCircle size={22} weight="fill" className="text-amber-600" />}
          <span className="font-display font-bold text-lg">
            {configured ? "Twilio är anslutet" : "Twilio inte konfigurerat ännu"}
          </span>
        </div>
        {!configured && (
          <p className="mt-2 text-sm text-amber-800">
            Lägg till <code className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">TWILIO_ACCOUNT_SID</code> och{" "}
            <code className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">TWILIO_AUTH_TOKEN</code> i backend och starta om tjänsten.
            Flödet och kopplingarna nedan fungerar direkt när nycklarna är på plats.
          </p>
        )}
        <div className="mt-4 rounded-lg bg-white/70 border border-black/5 p-4">
          <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">Webhook-URL för Twilio</div>
          <p className="text-sm text-zinc-600 mb-2">Sätt detta som "A call comes in" (HTTP POST) på ditt Twilio-nummer:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-sm bg-zinc-900 text-zinc-100 rounded-lg px-3 py-2 overflow-x-auto" data-testid="telephony-webhook-url">
              {status.webhook_url}
            </code>
            <button onClick={() => copy(status.webhook_url)} data-testid="copy-webhook-button"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-zinc-50">
              <Copy size={15} weight="bold" /> Kopiera
            </button>
          </div>
        </div>
      </div>

      {/* Zisson bridge setup */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-xl p-6" data-testid="zisson-setup">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><PhoneCall size={18} weight="bold" className="text-[#0033FF]" /> Zisson → AI-röst</h3>
        <p className="text-sm text-zinc-500 mt-1">Använder ni Zisson för er telefoni? Dirigera valda samtal till AI-agenten via en SIP-/vidarekopplingsbrygga — ingen ändring i röstmotorn behövs.</p>
        <ol className="mt-4 space-y-2.5 text-sm text-zinc-700 list-decimal list-inside">
          <li>Skapa/använd ett <b>Twilio-nummer</b> (eller en Twilio SIP-URI) och sätt webhook-URL:en ovan på det.</li>
          <li>I <b>Zisson-portalen</b>: dirigera en kö eller ett nummer (t.ex. efter öppettider eller en "prova AI"-kö) vidare till Twilio-numret/SIP-URI:n via <b>SIP-trunk</b> (rekommenderas) eller vidarekoppling.</li>
          <li>Lägg till samma nummer nedan och välj leverantör <b>Zisson</b> — då märks samtalen som Zisson i loggen.</li>
          <li>Ring in via Zisson och testa. Börja med en kö för att verifiera lugnt innan ni skalar upp.</li>
        </ol>
        <p className="mt-3 text-xs text-zinc-400">Extra latens från bryggan är ~50–150 ms (försumbart mot AI-svarstiden). För att koppla tillbaka ett pågående samtal till en mänsklig Zisson-agent krävs en separat påbyggnad.</p>
      </div>

      {/* Number → agent mapping */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-xl p-6">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><PhoneCall size={18} weight="bold" className="text-[#0033FF]" /> Telefonnummer → agent</h3>
        <p className="text-sm text-zinc-500 mt-1">Koppla ett Twilio-nummer till den AI-agent som ska svara på det.</p>

        <form onSubmit={addNumber} className="mt-4 grid sm:grid-cols-[1.1fr_1fr_0.8fr_0.9fr_auto] gap-3 items-end" data-testid="add-number-form">
          <div>
            <label className="block text-xs font-semibold text-zinc-500 mb-1">Telefonnummer (E.164)</label>
            <input data-testid="number-input" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })}
              placeholder="+46812345678"
              className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-[#0033FF] focus:ring-1 focus:ring-[#0033FF] outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-500 mb-1">Agent</label>
            <select data-testid="agent-select" value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
              className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-[#0033FF] focus:ring-1 focus:ring-[#0033FF] outline-none">
              <option value="">Välj agent…</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}{a.status !== "live" ? " (ej live)" : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-500 mb-1">Leverantör</label>
            <select data-testid="provider-select" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}
              className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-[#0033FF] focus:ring-1 focus:ring-[#0033FF] outline-none">
              <option value="twilio">Twilio (direkt)</option>
              <option value="zisson">Zisson (brygga)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-500 mb-1">Etikett (valfritt)</label>
            <input data-testid="label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="t.ex. Support sv"
              className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-[#0033FF] focus:ring-1 focus:ring-[#0033FF] outline-none" />
          </div>
          <button type="submit" disabled={adding} data-testid="add-number-button"
            className="inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-lg px-4 py-2 font-semibold text-sm disabled:opacity-50 h-[38px]">
            {adding ? <CircleNotch size={16} className="animate-spin" /> : <Plus size={16} weight="bold" />} Koppla
          </button>
        </form>

        <div className="mt-5 divide-y divide-zinc-100">
          {numbers.length === 0 ? (
            <p className="text-sm text-zinc-400 py-3" data-testid="numbers-empty">Inga nummer kopplade ännu.</p>
          ) : numbers.map((n) => {
            const ag = agents.find((a) => a.id === n.agent_id);
            const maxMin = ag?.max_call_seconds ? Math.round(ag.max_call_seconds / 60) : 0;
            return (
            <div key={n.id} className="flex items-center gap-3 py-3" data-testid="number-row">
              <div className="h-9 w-9 rounded-lg bg-[#0033FF]/10 flex items-center justify-center"><Phone size={16} weight="bold" className="text-[#0033FF]" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-2">{n.number}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${n.provider === "zisson" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`} data-testid="number-provider-badge">
                    {n.provider === "zisson" ? "Zisson" : "Twilio"}
                  </span>
                  {n.label ? <span className="text-xs text-zinc-400 font-normal">{n.label}</span> : null}
                </div>
                <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><Robot size={12} weight="bold" /> {n.agent_name || "—"}</span>
                  {ag && ag.status !== "live" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="number-agent-draft">Ej live</span>}
                  {maxMin > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600" data-testid="number-max-length">Max {maxMin} min</span>}
                </div>
              </div>
              <button onClick={() => removeNumber(n.id)} data-testid="delete-number-button"
                className="shrink-0 text-zinc-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50">
                <Trash size={16} weight="bold" />
              </button>
            </div>
            );
          })}
        </div>
      </div>

      {/* Call intelligence: reasons + failed queries */}
      {insights && (insights.reasons.length > 0 || insights.failed_count > 0) && (
        <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="telephony-insights">
          <div className="bg-white border border-zinc-200 rounded-xl p-6">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Tag size={18} weight="bold" className="text-[#0033FF]" /> Samtalsorsaker</h3>
            <p className="text-sm text-zinc-500 mt-1">AI kategoriserar varför kunderna ringer.</p>
            <div className="mt-4 space-y-2" data-testid="call-reasons">
              {insights.reasons.length === 0 ? <p className="text-sm text-zinc-400">Inga analyserade samtal ännu.</p> : (() => {
                const max = Math.max(...insights.reasons.map((r) => r.count));
                return insights.reasons.map((r) => (
                  <div key={r.topic} className="flex items-center gap-3" data-testid="reason-row">
                    <div className="w-40 truncate text-sm text-zinc-700 capitalize" title={r.topic}>{r.topic}</div>
                    <div className="flex-1 h-2.5 rounded-full bg-zinc-100 overflow-hidden">
                      <div className="h-full bg-[#0033FF] rounded-full" style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
                    </div>
                    <div className="w-8 text-right text-sm font-semibold text-zinc-500">{r.count}</div>
                  </div>
                ));
              })()}
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-xl p-6">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><WarningCircle size={18} weight="bold" className="text-amber-500" /> Samtal boten inte kunde lösa
              {insights.failed_count > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{insights.failed_count}</span>}</h3>
            <p className="text-sm text-zinc-500 mt-1">Olösta eller lågkvalitativa samtal — kunskapsluckor att täppa till.</p>
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto scroll-thin" data-testid="failed-calls">
              {insights.failed.length === 0 ? <p className="text-sm text-zinc-400">Inga olösta samtal — bra jobbat! 🎯</p> :
                insights.failed.map((f) => {
                  const d = drafts[f.id];
                  return (
                  <div key={f.id} data-testid="failed-call-row" className="rounded-lg border border-zinc-200 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <button onClick={() => setOpenCall(f.id)} className="text-left flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-zinc-800">{f.from || "Okänt"}</span>
                          {f.topic && <span className="text-xs text-zinc-400 capitalize">{f.topic}</span>}
                        </div>
                        {f.summary && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{f.summary}</p>}
                      </button>
                      <button data-testid="fix-call-button" onClick={() => fixCall(f.id)} disabled={d?.loading}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 text-xs font-bold disabled:opacity-60">
                        {d?.loading ? <CircleNotch size={13} className="animate-spin" /> : <Sparkle size={13} weight="bold" />} Åtgärda
                      </button>
                    </div>
                    {d?.items && d.items.length > 0 && (
                      <div className="mt-3 space-y-2" data-testid="kb-drafts">
                        {d.items.map((s) => (
                          <div key={s.id} className="rounded-lg bg-emerald-50/60 border border-emerald-200 p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-600">Förslag till kunskapsbas</div>
                            <p className="text-sm font-semibold text-zinc-800 mt-1">{s.question}</p>
                            <p className="text-xs text-zinc-600 mt-1 line-clamp-4">{s.answer}</p>
                            <div className="flex gap-2 mt-2">
                              <button data-testid="kb-draft-accept" onClick={() => acceptDraft(f.id, d.agentId, s.id)}
                                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 text-xs font-bold">
                                <CheckCircle size={12} weight="bold" /> Lägg till i kunskapsbasen
                              </button>
                              <button data-testid="kb-draft-dismiss" onClick={() => dismissDraft(f.id, d.agentId, s.id)}
                                className="rounded-md border border-zinc-300 px-2.5 py-1 text-xs font-semibold text-zinc-500 hover:bg-zinc-50">Avfärda</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Call logs */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between gap-3">
          <span className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Samtalslogg</span>
          <input data-testid="call-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Sök nummer, agent, ämne, sammanfattning, transkript…"
            className="w-72 max-w-[60%] rounded-lg border border-zinc-200 px-3 py-1.5 text-sm focus:border-[#0033FF] focus:ring-1 focus:ring-[#0033FF] outline-none" />
        </div>
        {(() => {
          const q = query.trim().toLowerCase();
          const filtered = !q ? calls : calls.filter((c) => {
            const hay = [c.from, c.agent_name, c.summary, c.topic, ...(c.tags || []),
              ...((c.turns || []).map((t) => t.content))].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
          });
          return filtered.length === 0 ? (
          <p className="text-sm text-zinc-400 px-4 py-6" data-testid="calls-empty">{q ? "Inga samtal matchar sökningen." : "Inga samtal ännu. När någon ringer ett kopplat nummer dyker samtalet upp här."}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider border-b border-zinc-100">
                <th className="py-3 px-4 font-semibold w-8"></th>
                <th className="py-3 px-4 font-semibold">Från</th>
                <th className="py-3 px-4 font-semibold">Agent</th>
                <th className="py-3 px-4 font-semibold">Status</th>
                <th className="py-3 px-4 font-semibold text-right">Längd</th>
                <th className="py-3 px-4 font-semibold text-right">Tid</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <Fragment key={c.id}>
                  <tr className="border-b border-zinc-50 last:border-0 cursor-pointer hover:bg-zinc-50"
                    data-testid="call-row" onClick={() => setOpenCall(openCall === c.id ? null : c.id)}>
                    <td className="py-3 px-4 text-zinc-400">{openCall === c.id ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}</td>
                    <td className="py-3 px-4 font-medium">{c.from || "Okänt"}
                      {c.provider === "zisson" && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">Zisson</span>}
                    </td>
                    <td className="py-3 px-4 text-zinc-600">{c.agent_name || "—"}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.status === "completed" ? "bg-zinc-100 text-zinc-600" : "bg-emerald-100 text-emerald-700"}`}>
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                      {c.qa?.overall != null && (
                        <span className={`ml-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${qualityColor(c.qa.overall)}`} data-testid="call-quality-badge">
                          Kvalitet {c.qa.overall}
                        </span>
                      )}
                      {c.handover && (
                        <span className="ml-1.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700" data-testid="call-handover-badge">
                          Kopplad till människa
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-zinc-500">{fmtDuration(c)}</td>
                    <td className="py-3 px-4 text-right text-zinc-400 text-xs">{c.started_at ? new Date(c.started_at).toLocaleString("sv-SE") : "—"}</td>
                  </tr>
                  {openCall === c.id && (
                    <tr data-testid="call-transcript">
                      <td colSpan={6} className="bg-zinc-50 px-4 py-4">
                        {c.recording_url && (
                          <div className="mb-4" data-testid="call-recording">
                            <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-1.5">Inspelning</div>
                            <audio controls src={`${BACKEND}${c.recording_url}`} className="w-full max-w-md h-9" />
                          </div>
                        )}

                        <div className="mb-4" data-testid="call-summary">
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">AI-sammanfattning</div>
                            {(c.turns || []).length > 0 && (
                              <button onClick={() => analyzeCall(c.id)} disabled={analyzing === c.id} data-testid="analyze-call-button"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0033FF] hover:text-[#002BE6] disabled:opacity-50">
                                {analyzing === c.id ? <CircleNotch size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" />}
                                {c.summary ? "Uppdatera" : "Generera sammanfattning"}
                              </button>
                            )}
                          </div>
                          {c.summary ? (
                            <div className="rounded-lg border border-zinc-200 bg-white p-3">
                              <p className="text-sm text-zinc-700">{c.summary}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                                {c.qa?.overall != null && (
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${qualityColor(c.qa.overall)}`}>Kvalitet {c.qa.overall}/100</span>
                                )}
                                {c.resolved != null && (
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.resolved ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                    {c.resolved ? "Löst" : "Ej löst"}
                                  </span>
                                )}
                                {c.topic && <span className="inline-flex items-center gap-1 text-xs text-zinc-500"><Tag size={12} weight="bold" />{c.topic}</span>}
                                {(c.tags || []).slice(0, 4).map((t, k) => <span key={k} className="text-xs text-zinc-400">#{t}</span>)}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-zinc-400">Ingen sammanfattning ännu.{(c.turns || []).length > 0 ? " Klicka på “Generera sammanfattning”." : ""}</p>
                          )}
                        </div>

                        <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-1.5">Transkript</div>
                        {c.handover && (
                          <p className="text-xs text-violet-600 mb-2" data-testid="call-handover-target">Kopplades till människa: <span className="font-mono">{c.handover_target || "—"}</span></p>
                        )}
                        {(c.turns || []).length === 0 ? (
                          <p className="text-sm text-zinc-400">Inget transkript.</p>
                        ) : (
                          <div className="space-y-2 max-w-2xl">
                            {c.turns.map((t, i) => (
                              <div key={i} className={`flex ${t.role === "user" ? "justify-start" : "justify-end"}`}>
                                <div className={`rounded-lg px-3 py-1.5 text-sm max-w-[80%] ${t.role === "user" ? "bg-white border border-zinc-200" : "bg-[#0033FF] text-white"}`}>
                                  {t.content}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        );
        })()}
      </div>
    </div>
  );
}
